// A chart on a slide (SD, 26 Oct 2026, David's Features 4: "native charts made from typed data or a sheet board, styled
// to the deck's theme"). One box, moved, resized and layered as any box (shared/slideDesign.ts), holding the deck's own
// chart: its type (PowerPoint's everyday set: column, bar, line, area, pie, doughnut, scatter, and a combo of columns
// and a line on one axis), its categories and series, and its format (titles, the legend's place, data labels,
// gridlines, colours). Its colours are the deck's theme colours by name, so a theme change recolours it, and its words
// are the deck's fonts.
//
// Its numbers are the user's: typed in the chart's data grid, read from one of their sheet boards (`link`: the board,
// tab and range, never more of the sheet than the chart shows; the values kept here are the ones it shows, so a shared
// link and a printed page draw it without the sheet), or an imported PowerPoint chart's own cached values. Nothing is
// ever made up: a chart with no numbers says "Add your data".
//
// Plain data only, as every slide's: no CSS, addresses or HTML.

import { cleanColor, colorValue, isThemeColor, type Palette, type SlideColor, type ThemeColorId } from "./slideDesign";
import { rectName, type Rect } from "./sheet/addr";

export const CHART_TYPES = ["column", "bar", "line", "area", "pie", "doughnut", "scatter", "combo"] as const;
export type ChartType = (typeof CHART_TYPES)[number];
export const CHART_NAMES: Record<ChartType, string> = {
  column: "Column",
  bar: "Bar",
  line: "Line",
  area: "Area",
  pie: "Pie",
  doughnut: "Doughnut",
  scatter: "Scatter",
  combo: "Column and line",
};

export const LEGEND_PLACES = ["bottom", "top", "right", "left", "none"] as const;
export type LegendPlace = (typeof LEGEND_PLACES)[number];

/** How a chart's series are coloured: the theme's accents in turn (PowerPoint's Colorful), or shades of one theme colour. */
export type ChartColors = "colorful" | ThemeColorId;

export interface ChartSeries {
  name: string;
  /** one value a category; null where the data has none (a gap, never a 0) */
  values: (number | null)[];
  /** its own colour, over the chart's colours */
  color?: SlideColor;
  /** in a combo chart: drawn as a line over the columns */
  line?: boolean;
}

/** Where a chart's numbers come from on one of the user's sheet boards: the board, the tab and a block of cells. */
export interface SheetLink {
  board: string;
  tab: string;
  /** a block as A1:C13 */
  range: string;
  /** series in rows rather than columns (PowerPoint's Switch Row/Column) */
  rows?: boolean;
}

export interface SlideChart {
  type: ChartType;
  /** the categories, across (a scatter chart's X values, as typed) */
  cats: string[];
  series: ChartSeries[];
  /** the chart's own title, over it */
  title?: string;
  /** the axes' titles: across (categories) and up (values) */
  xTitle?: string;
  yTitle?: string;
  /** where the legend sits; unset, at the bottom when there are two series or more (or slices), else none */
  legend?: LegendPlace;
  /** each value written on its mark */
  labels?: boolean;
  /** the value axis's gridlines: unset is on */
  grid?: boolean;
  /** columns, bars and areas stacked rather than side by side */
  stacked?: boolean;
  /** a line drawn as a smooth curve */
  smooth?: boolean;
  colors?: ChartColors;
  /** what the values are written with, read from what was typed or what the sheet shows ($, %) */
  pre?: string;
  post?: string;
  /** the decimals the values are written with, when the sheet says (unset: as each value needs) */
  dp?: number;
  /** read from a sheet board: where, and when its values were last read */
  link?: SheetLink;
  at?: number;
  /** a linked chart whose sheet, tab or range is gone: why, in words, drawn in place of its numbers */
  lost?: string;
}

/** The most series, categories and characters a chart holds. */
export const MAX_SERIES = 12;
export const MAX_CATS = 100;
export const MAX_CHART_NAME = 80;
export const MAX_CHART_TITLE = 160;

const ID = /^[A-Za-z0-9_-]{4,40}$/;
const RANGE = /^[A-Z]{1,3}\d{1,5}(:[A-Z]{1,3}\d{1,5})?$/;

const words = (v: unknown, max: number): string | undefined => {
  if (typeof v !== "string" && typeof v !== "number") return undefined;
  const s = String(v).replace(/[\u0000-\u001f\u007f]+/g, " ").replace(/\s+/g, " ").trim();
  return s ? s.slice(0, max) : undefined;
};

/** A value as kept: a finite number (rounded to ten places, so 0.1 + 0.2 reads as 0.3), or null. */
export function cleanValue(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? Math.round(v * 1e10) / 1e10 : null;
  if (typeof v === "string") return parseValue(v).n;
  return null;
}

/** A chart as kept, or null when it cannot be one (no type it knows). Series and categories are cut to the limits. */
export function cleanChart(raw: unknown): SlideChart | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const type = (CHART_TYPES as readonly unknown[]).includes(r.type) ? (r.type as ChartType) : null;
  if (!type) return null;
  const catsRaw = Array.isArray(r.cats) ? r.cats.slice(0, MAX_CATS) : [];
  const seriesRaw = Array.isArray(r.series) ? r.series.slice(0, MAX_SERIES) : [];
  const n = Math.min(MAX_CATS, Math.max(catsRaw.length, ...seriesRaw.map((s) => (s && typeof s === "object" && Array.isArray((s as ChartSeries).values) ? (s as ChartSeries).values.length : 0))));
  const cats = Array.from({ length: n }, (_, i) => words(catsRaw[i], MAX_CHART_NAME) ?? "");
  const series: ChartSeries[] = seriesRaw.flatMap((s, i) => {
    if (!s || typeof s !== "object" || Array.isArray(s)) return [];
    const o = s as Record<string, unknown>;
    const vals = Array.isArray(o.values) ? o.values : [];
    const out: ChartSeries = { name: words(o.name, MAX_CHART_NAME) ?? `Series ${i + 1}`, values: Array.from({ length: n }, (_, j) => cleanValue(vals[j])) };
    const color = cleanColor(o.color);
    if (color) out.color = color;
    if (o.line === true && type === "combo") out.line = true;
    return [out];
  });
  const chart: SlideChart = { type, cats, series };
  const title = words(r.title, MAX_CHART_TITLE);
  if (title) chart.title = title;
  const xTitle = words(r.xTitle, MAX_CHART_NAME);
  const yTitle = words(r.yTitle, MAX_CHART_NAME);
  if (xTitle) chart.xTitle = xTitle;
  if (yTitle) chart.yTitle = yTitle;
  if ((LEGEND_PLACES as readonly unknown[]).includes(r.legend)) chart.legend = r.legend as LegendPlace;
  if (r.labels === true) chart.labels = true;
  if (r.grid === false) chart.grid = false;
  if (r.stacked === true && (type === "column" || type === "bar" || type === "area")) chart.stacked = true;
  if (r.smooth === true && (type === "line" || type === "area" || type === "scatter" || type === "combo")) chart.smooth = true;
  if (r.colors === "colorful") chart.colors = "colorful";
  else if (isThemeColor(r.colors)) chart.colors = r.colors;
  const pre = words(r.pre, 4);
  const post = words(r.post, 4);
  if (pre) chart.pre = pre;
  if (post) chart.post = post;
  if (typeof r.dp === "number" && Number.isInteger(r.dp) && r.dp >= 0 && r.dp <= 6) chart.dp = r.dp;
  // a combo chart's last series is its line when none is said
  if (type === "combo" && chart.series.length > 1 && !chart.series.some((s) => s.line)) chart.series[chart.series.length - 1].line = true;
  const link = cleanSheetLink(r.link);
  if (link) {
    chart.link = link;
    if (typeof r.at === "number" && Number.isFinite(r.at) && r.at > 0) chart.at = Math.round(r.at);
  }
  // a shared link's visitor keeps the words of a chart whose sheet is gone, without the link (publicCharts)
  const lost = words(r.lost, 200);
  if (lost) chart.lost = lost;
  return chart;
}

export function cleanSheetLink(raw: unknown): SheetLink | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.board !== "string" || !ID.test(r.board) || typeof r.tab !== "string" || !ID.test(r.tab)) return null;
  const range = typeof r.range === "string" ? r.range.trim().toUpperCase().replace(/\$/g, "") : "";
  if (!RANGE.test(range)) return null;
  return { board: r.board, tab: r.tab, range, ...(r.rows === true ? { rows: true } : {}) };
}

/** Why a chart cannot be kept, in words a person or an agent reads; null when it can. */
export function chartLimits(c: SlideChart, id: string): string | null {
  if (c.series.length > MAX_SERIES) return `A chart holds up to ${MAX_SERIES} series; chart ${id} would have ${c.series.length}.`;
  if (c.cats.length > MAX_CATS) return `A chart holds up to ${MAX_CATS} categories; chart ${id} would have ${c.cats.length}.`;
  return null;
}

/** Whether a chart has any number to draw. */
export const chartHasData = (c: SlideChart | undefined | null): boolean => !!c && c.series.some((s) => s.values.some((v) => v !== null));

/** Pie and doughnut charts draw one series as slices; the rest draw axes. */
export const isRound = (t: ChartType) => t === "pie" || t === "doughnut";

/** The legend's place as drawn: what the chart says, or at the bottom when there is more than one thing to tell apart. */
export function legendOf(c: SlideChart): LegendPlace {
  if (c.legend) return c.legend;
  if (isRound(c.type)) return "right";
  return c.series.length > 1 ? "bottom" : "none";
}

// ---------------------------------------------------------------------------
// colours

const ACCENTS: ThemeColorId[] = ["accent", "accent2", "accent3", "accent4"];

/**
 * Series i's colour as the deck draws it: its own, else the chart's colours: the theme's four accents in turn, then
 * the same four lighter and darker (PowerPoint's Colorful goes through its accents, then their tints and shades); or
 * shades of one theme colour, darkest first. Pie and doughnut slices take these one a slice.
 */
export function seriesColor(c: Pick<SlideChart, "colors">, i: number, palette: Palette, own?: SlideColor): string {
  if (own) return colorValue(own, palette, palette.accent);
  const scheme = c.colors ?? "colorful";
  if (scheme === "colorful") {
    const base = palette[ACCENTS[i % 4]];
    const round = Math.floor(i / 4) % 3;
    return round === 0 ? base : round === 1 ? mix(base, palette.background, 0.45) : mix(base, palette.text, 0.35);
  }
  const base = palette[scheme];
  // one hue in steps from the colour itself toward the slide, then back toward the text
  const steps = [0, 0.32, 0.55, -0.3, 0.72, -0.5];
  const t = steps[i % steps.length];
  return t >= 0 ? mix(base, palette.background, t) : mix(base, palette.text, -t);
}

/** The colours a chart offers, as named in its menu. */
export const CHART_COLOR_CHOICES: { id: ChartColors; name: string }[] = [
  { id: "colorful", name: "Theme colors" },
  { id: "accent", name: "Shades of the accent" },
  { id: "accent2", name: "Shades of accent 2" },
  { id: "accent3", name: "Shades of accent 3" },
  { id: "accent4", name: "Shades of accent 4" },
  { id: "text", name: "Shades of the text" },
];

function mix(a: string, b: string, t: number): string {
  const p = (h: string) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(h.trim());
    return m ? [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16)) : null;
  };
  const x = p(a);
  const y = p(b);
  if (!x || !y) return a;
  return "#" + x.map((v, i) => Math.round(v + (y[i] - v) * t).toString(16).padStart(2, "0")).join("").toUpperCase();
}

// ---------------------------------------------------------------------------
// values as typed and as written

/**
 * A value as a person types it: 1200, 1,200, $1.2k, 15%, (300) or -300, €4,5 is not read (one decimal mark: a dot).
 * n is null for anything that is not a number (words, an empty cell); pre and post are a currency sign or a %.
 */
export function parseValue(raw: string): { n: number | null; pre?: string; post?: string } {
  let s = raw.trim();
  if (!s) return { n: null };
  let neg = false;
  if (/^\(.*\)$/.test(s)) {
    neg = true;
    s = s.slice(1, -1).trim();
  }
  if (s.startsWith("-") || s.startsWith("−")) {
    neg = !neg;
    s = s.slice(1).trim();
  }
  let pre: string | undefined;
  let post: string | undefined;
  const cur = /^(US\$|A\$|C\$|\$|£|€|¥|₹)\s?/.exec(s);
  if (cur) {
    pre = cur[1];
    s = s.slice(cur[0].length);
  }
  if (s.startsWith("-") || s.startsWith("−")) {
    neg = !neg;
    s = s.slice(1);
  }
  let k = 1;
  const pct = /%$/.test(s);
  if (pct) {
    post = "%";
    s = s.slice(0, -1).trim();
  } else {
    const m = /^(.*\d)\s*([kKmMbB])$/.exec(s);
    if (m) {
      k = { k: 1e3, m: 1e6, b: 1e9 }[m[2].toLowerCase() as "k" | "m" | "b"];
      s = m[1];
    }
  }
  if (!/^\d{1,3}(,\d{3})*(\.\d+)?$|^\d+(\.\d+)?$|^\.\d+$/.test(s)) return { n: null };
  const n = Number(s.replace(/,/g, "")) * k * (neg ? -1 : 1);
  if (!Number.isFinite(n)) return { n: null };
  return { n: Math.round(n * 1e10) / 1e10, ...(pre ? { pre } : {}), ...(post ? { post } : {}) };
}

/** A value written for a person: 1,200, 12.5K, 1.4M, with the chart's sign; compact on an axis. */
export function formatValue(n: number, c: Pick<SlideChart, "pre" | "post" | "dp">, compact = false): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  let body: string;
  if (compact && abs >= 1e3) {
    const [d, u] = abs >= 1e9 ? [1e9, "B"] : abs >= 1e6 ? [1e6, "M"] : [1e3, "K"];
    const v = abs / d;
    body = `${trim(v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2))}${u}`;
  } else if (c.dp !== undefined) body = abs.toLocaleString("en-US", { minimumFractionDigits: c.dp, maximumFractionDigits: c.dp });
  else body = abs.toLocaleString("en-US", { maximumFractionDigits: abs >= 100 ? 1 : abs >= 1 ? 2 : 4 });
  return `${sign}${c.pre ?? ""}${body}${c.post ?? ""}`;
}

const trim = (s: string) => (s.includes(".") ? s.replace(/\.?0+$/, "") : s);

/** An axis's even steps over the values: from lo to hi in step, where 0 is on it when the values are all one side. */
export function niceScale(min: number, max: number, want = 5): { lo: number; hi: number; step: number } {
  let lo = Math.min(0, min);
  let hi = Math.max(0, max);
  if (lo === hi) {
    hi = lo === 0 ? 1 : Math.abs(lo) * 1.2 + lo;
    if (lo > 0) lo = 0;
  }
  const raw = (hi - lo) / Math.max(1, want);
  const p = 10 ** Math.floor(Math.log10(raw));
  const f = raw / p;
  const step = nice(f) * p;
  return { lo: Math.floor(lo / step + 1e-9) * step, hi: Math.ceil(hi / step - 1e-9) * step, step };
}

/** The nearest of the even steps (1, 2, 2.5, 5, 10) to a step of f between 1 and 10. */
const nice = (f: number) => (f < 1.5 ? 1 : f < 2.25 ? 2 : f < 3.5 ? 2.5 : f < 7.5 ? 5 : 10);

/** An X axis's even steps for a scatter chart, which need not start at 0. */
export function niceRange(min: number, max: number, want = 5): { lo: number; hi: number; step: number } {
  if (min === max) return niceScale(Math.min(0, min), Math.max(0, max) || 1, want);
  const raw = (max - min) / Math.max(1, want);
  const p = 10 ** Math.floor(Math.log10(raw));
  const f = raw / p;
  const step = nice(f) * p;
  return { lo: Math.floor(min / step) * step, hi: Math.ceil(max / step) * step, step };
}

// ---------------------------------------------------------------------------
// the chart's data as rows (its data grid, an agent's Markdown table): the first row the series' names, the first
// column the categories

/** The chart's data as rows of cells: a corner, then the series' names; then a row a category. */
export function chartRows(c: SlideChart): string[][] {
  const head = ["", ...c.series.map((s) => s.name)];
  return [head, ...c.cats.map((cat, i) => [cat, ...c.series.map((s) => (s.values[i] === null || s.values[i] === undefined ? "" : String(s.values[i])))])];
}

/**
 * Rows of cells (a grid typed in, a Markdown table, a sheet's block as shown) as a chart's data: the first row names
 * the series, the first column the categories. Values as typed ($1,200, 15%) are read as numbers, and a sign every
 * value shares is kept for writing them. Empty rows and columns at the end are left out.
 */
export function dataFromRows(rows0: string[][], opts: { head?: boolean; rows?: boolean } = {}): Pick<SlideChart, "cats" | "series" | "pre" | "post"> {
  let rows = rows0.map((r) => r.map((c) => (c ?? "").toString()));
  if (opts.rows) rows = transposeGrid(rows);
  while (rows.length && rows[rows.length - 1].every((c) => !c.trim())) rows.pop();
  const width = Math.max(0, ...rows.map((r) => r.length));
  let cols = width;
  while (cols > 0 && rows.every((r) => !(r[cols - 1] ?? "").trim())) cols--;
  if (!rows.length || !cols) return { cats: [], series: [] };
  const head = opts.head ?? true;
  const names = head ? rows[0] : [];
  const body = head ? rows.slice(1) : rows;
  const one = cols === 1;
  const pres = new Set<string>();
  const posts = new Set<string>();
  let seen = 0;
  const series: ChartSeries[] = [];
  for (let c = one ? 0 : 1; c < cols && series.length < MAX_SERIES; c++) {
    // a column of words or dates (a region, a day) is no series: only numbers are drawn (Fixes 10)
    const cells = body.slice(0, MAX_CATS).map((r) => r[c] ?? "");
    if (!one && cells.some((x) => x.trim()) && cells.every((x) => parseValue(x).n === null)) continue;
    const values = cells.map((x) => {
      const p = parseValue(x);
      if (p.n !== null) {
        seen++;
        pres.add(p.pre ?? "");
        posts.add(p.post ?? "");
      }
      return p.n;
    });
    series.push({ name: words(names[c], MAX_CHART_NAME) ?? `Series ${series.length + 1}`, values });
  }
  const cats = body.slice(0, MAX_CATS).map((r) => (one ? "" : (words(r[0], MAX_CHART_NAME) ?? "")));
  const pre = seen && pres.size === 1 ? [...pres][0] : "";
  const post = seen && posts.size === 1 ? [...posts][0] : "";
  return { cats, series, ...(pre ? { pre } : {}), ...(post ? { post } : {}) };
}

export function transposeGrid(rows: string[][]): string[][] {
  const w = Math.max(0, ...rows.map((r) => r.length));
  return Array.from({ length: w }, (_, c) => rows.map((r) => r[c] ?? ""));
}

/** The chart with its series and categories swapped (PowerPoint's Switch Row/Column). */
export function switchRowsCols(c: SlideChart): SlideChart {
  const series: ChartSeries[] = c.cats.slice(0, MAX_SERIES).map((cat, i) => ({ name: cat || `Series ${i + 1}`, values: c.series.map((s) => s.values[i] ?? null) }));
  return { ...c, cats: c.series.map((s) => s.name), series };
}

/** The chart's data as a Markdown table, for agents. */
export function chartToMarkdown(c: SlideChart): string {
  const rows = chartRows(c);
  const cell = (s: string) => s.replace(/\|/g, "\\|") || " ";
  return [`| ${rows[0].map((x, i) => (i === 0 ? cell(c.type === "scatter" ? "X" : x || "Category") : cell(x))).join(" | ")} |`, `|${rows[0].map(() => "---").join("|")}|`, ...rows.slice(1).map((r) => `| ${r.map(cell).join(" | ")} |`)].join("\n");
}

/** A new chart of a type, with nothing in it yet: it says "Add your data" until there is some. */
export function emptyChart(type: ChartType): SlideChart {
  if (isRound(type)) return { type, cats: ["", "", ""], series: [{ name: "Series 1", values: [null, null, null] }] };
  return { type, cats: ["", "", "", ""], series: [{ name: "Series 1", values: [null, null, null, null] }, ...(type === "combo" ? [{ name: "Series 2", values: [null, null, null, null], line: true }] : [])] };
}

// ---------------------------------------------------------------------------
// a shared link's visitors

/** Whether a slide's boxes hold a chart linked to a sheet. */
export const hasLinkedChart = (boxes: unknown): boolean => Array.isArray(boxes) && boxes.some((b) => !!(b as { chart?: { link?: unknown } } | null)?.chart?.link);

/** What a shared link's visitor reads on a linked chart whose sheet, tab or range is gone: no sheet, tab or range named. */
export const PUBLIC_LOST = "This chart's numbers are not available.";

/**
 * A slide's boxes as a shared link's visitor gets them: a linked chart keeps the numbers it shows and loses its link
 * (which sheet, tab and range); one whose sheet is gone has no numbers, and the owner's words for why (which name the
 * sheet, its tab and the range) become words that name none of them.
 */
export function publicCharts(boxes: unknown[]): unknown[] {
  return boxes.map((b) => {
    const c = (b as { chart?: SlideChart } | null)?.chart;
    if (!c?.link) return b;
    const { link: _l, at: _a, lost, ...rest } = c;
    return { ...(b as object), chart: lost ? { ...rest, lost: PUBLIC_LOST, series: rest.series.map((s) => ({ ...s, values: s.values.map(() => null) })) } : rest };
  });
}

// ---------------------------------------------------------------------------
// a block of a sheet's cells as a chart's data (a linked slide chart, and a chart on the sheet itself)

export type LinkedData = Pick<SlideChart, "cats" | "series" | "pre" | "post">;

/** What a link reads now: the chart's data, or why there is none (in words a person reads). */
export type LinkRead = { data: LinkedData; range: string } | { lost: string };

/**
 * A block of a sheet's cells as a chart's data, read as the sheet shows them (show), at most a chart's worth of rows and
 * columns and only as far as the cells in use: the first row names the series when it holds words, the first column the
 * categories (Switch: rows). Or why there is none, in words. Shared by a linked slide chart and a chart on the sheet
 * itself (Fixes 9, H2b), so the two read a block one way.
 */
export function blockData(show: (r: number, c: number) => string, rect: Rect, used: { rows: number; cols: number }, rows: boolean, tabName: string): LinkRead {
  // a block past the cells in use reads only what is in use
  const r: Rect = { r0: rect.r0, c0: rect.c0, r1: Math.min(rect.r1, Math.max(rect.r0, used.rows - 1)), c1: Math.min(rect.c1, Math.max(rect.c0, used.cols - 1)) };
  const across = rows ? MAX_CATS + 1 : MAX_SERIES + 1;
  const down = rows ? MAX_SERIES + 1 : MAX_CATS + 1;
  const grid: string[][] = [];
  for (let i = r.r0; i <= Math.min(r.r1, r.r0 + down - 1); i++) {
    const row: string[] = [];
    for (let j = r.c0; j <= Math.min(r.c1, r.c0 + across - 1); j++) row.push(show(i, j));
    grid.push(row);
  }
  if (!grid.some((row) => row.some((c) => c.trim()))) return { lost: `${rectName(rect)} on "${tabName}" is empty now.` };
  const data = dataFromRows(grid, { rows, head: hasHead(grid, rows) });
  if (!data.series.some((s) => s.values.some((v) => v !== null))) return { lost: `${rectName(rect)} on "${tabName}" holds no numbers.` };
  return { data, range: rectName(rect) };
}

/** Whether a block's first row (its first column, for series in rows) names the series: it holds words, not numbers. */
function hasHead(grid: string[][], rows: boolean): boolean {
  const line = rows ? grid.map((r) => r[0] ?? "") : (grid[0] ?? []);
  const cols = rows ? grid.length : (grid[0]?.length ?? 0);
  const vals = (cols > 1 ? line.slice(1) : line).filter((c) => c.trim());
  if (!vals.length) return cols > 1;
  return vals.some((c) => parseValue(c).n === null);
}

